import React, { useState, useEffect } from "react";
import { Plus, Search, Filter } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { StatusBadge } from "../components/common/StatusBadge";
import { QuickActions } from "../components/common/QuickActions";
import { CallLogModal } from "../components/common/CallLogModal";
import { CallHistoryModal } from "../components/common/CallHistoryModal";
import { useAuth } from "../context/AuthContext";
import { apiRequest, formatPhone, formatDate, leadSources } from "../lib/utils";
import { toast } from "sonner";

export const LeadsPage = () => {
  const { user, isAdmin } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [branches, setBranches] = useState([]);
  const [cres, setCres] = useState([]);
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [callLogContact, setCallLogContact] = useState(null);
  const [historyContact, setHistoryContact] = useState(null);
  
  // New lead form
  const [newLead, setNewLead] = useState({
    name: "",
    phone: "",
    email: "",
    source: "meta_ads",
    branch_id: "",
    assigned_cre_id: "",
    service_interest: "",
    notes: ""
  });

  useEffect(() => {
    fetchLeads();
    if (isAdmin()) {
      fetchBranches();
      fetchCREs();
    }
  }, [statusFilter]);

  const fetchLeads = async () => {
    try {
      let url = "/leads";
      const params = [];
      if (statusFilter && statusFilter !== "all") {
        params.push(`status=${statusFilter}`);
      }
      // For CRE users, use cre_user_id to filter by their assigned branches
      if (!isAdmin() && user?.id) {
        params.push(`cre_user_id=${user.id}`);
      }
      if (params.length > 0) {
        url += `?${params.join("&")}`;
      }
      const data = await apiRequest(url);
      setLeads(data);
    } catch (error) {
      toast.error("Failed to fetch leads");
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const data = await apiRequest("/branches");
      setBranches(data);
    } catch (error) {
      console.error("Failed to fetch branches:", error);
    }
  };

  const fetchCREs = async () => {
    try {
      const data = await apiRequest("/users?role=cre");
      setCres(data);
    } catch (error) {
      console.error("Failed to fetch CREs:", error);
    }
  };

  const handleAddLead = async (e) => {
    e.preventDefault();
    try {
      await apiRequest("/leads", {
        method: "POST",
        body: JSON.stringify(newLead)
      });
      toast.success("Lead added successfully");
      setShowAddModal(false);
      setNewLead({
        name: "",
        phone: "",
        email: "",
        source: "meta_ads",
        branch_id: "",
        assigned_cre_id: "",
        service_interest: "",
        notes: ""
      });
      fetchLeads();
    } catch (error) {
      toast.error(error.message || "Failed to add lead");
    }
  };

  const handleCall = (lead, skipDialer = false) => {
    // First open the phone dialer (unless skipped, e.g., from QuickActions which handles it)
    if (!skipDialer && lead.phone) {
      window.location.href = `tel:${lead.phone}`;
    }
    // Show call log modal after a brief delay to allow dialer to open
    setTimeout(() => {
      setCallLogContact({
        id: lead.id,
        name: lead.name,
        phone: lead.phone,
        status: lead.status,
        type: "lead",
        branch_id: lead.branch_id
      });
    }, skipDialer ? 0 : 800);
  };

  const handleViewHistory = (lead) => {
    setHistoryContact({
      id: lead.id,
      name: lead.name,
      type: "lead"
    });
  };

  const filteredLeads = leads.filter((lead) => {
    const matchesSearch =
      lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone?.includes(searchTerm) ||
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const statusOptions = [
    { value: "all", label: "All Statuses" },
    { value: "new_lead", label: "New Lead" },
    { value: "contacted", label: "Contacted" },
    { value: "follow_up", label: "Follow Up" },
    { value: "booked", label: "Booked" },
    { value: "converted", label: "Converted" },
    { value: "lost", label: "Lost" }
  ];

  return (
    <div className="space-y-6" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl font-bold text-foreground">
              {isAdmin() ? "All Leads" : "My Leads"}
            </h1>
            <Badge 
              className="bg-green-600 text-white text-sm font-bold px-3 py-1"
              data-testid="leads-total-count"
            >
              {loading ? "..." : leads.length}
            </Badge>
          </div>
          <p className="text-muted-foreground">
            Manage and track your leads
          </p>
        </div>
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogTrigger asChild>
            <Button data-testid="add-lead-button">
              <Plus className="w-4 h-4 mr-2" />
              Add Lead
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="font-heading">Add New Lead</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddLead} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={newLead.name}
                    onChange={(e) => setNewLead({ ...newLead, name: e.target.value })}
                    required
                    data-testid="lead-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    value={newLead.phone}
                    onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                    required
                    data-testid="lead-phone-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newLead.email}
                  onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                  data-testid="lead-email-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="source">Source</Label>
                  <Select
                    value={newLead.source}
                    onValueChange={(value) => setNewLead({ ...newLead, source: value })}
                  >
                    <SelectTrigger data-testid="lead-source-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {leadSources.map((src) => (
                        <SelectItem key={src.value} value={src.value}>
                          {src.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {isAdmin() && (
                  <div>
                    <Label htmlFor="branch">Branch</Label>
                    <Select
                      value={newLead.branch_id}
                      onValueChange={(value) => setNewLead({ ...newLead, branch_id: value })}
                    >
                      <SelectTrigger data-testid="lead-branch-select">
                        <SelectValue placeholder="Select branch" />
                      </SelectTrigger>
                      <SelectContent>
                        {branches.map((branch) => (
                          <SelectItem key={branch.id} value={branch.id}>
                            {branch.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              {isAdmin() && (
                <div>
                  <Label htmlFor="cre">Assign to CRE</Label>
                  <Select
                    value={newLead.assigned_cre_id}
                    onValueChange={(value) => setNewLead({ ...newLead, assigned_cre_id: value })}
                  >
                    <SelectTrigger data-testid="lead-cre-select">
                      <SelectValue placeholder="Select CRE" />
                    </SelectTrigger>
                    <SelectContent>
                      {cres.map((cre) => (
                        <SelectItem key={cre.id} value={cre.id}>
                          {cre.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label htmlFor="service">Service Interest</Label>
                <Input
                  id="service"
                  value={newLead.service_interest}
                  onChange={(e) => setNewLead({ ...newLead, service_interest: e.target.value })}
                  placeholder="e.g., Hair Treatment, Skin Care"
                  data-testid="lead-service-input"
                />
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={newLead.notes}
                  onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                  rows={3}
                  data-testid="lead-notes-input"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-lead-button">
                  Save Lead
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="bento-card">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, phone, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="lead-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card className="bento-card">
        <CardContent className="p-0">
          <div className="data-table-container">
            <Table>
              <TableHeader>
                <TableRow className="table-high-density">
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Follow-up</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredLeads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No leads found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLeads.map((lead) => (
                    <TableRow key={lead.id} className="table-high-density" data-testid={`lead-row-${lead.id}`}>
                      <TableCell className="font-medium">{lead.name}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {formatPhone(lead.phone)}
                      </TableCell>
                      <TableCell className="capitalize text-muted-foreground">
                        {lead.source?.replace("_", " ")}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={lead.status} />
                      </TableCell>
                      <TableCell className="text-sm">
                        {lead.next_follow_up ? formatDate(lead.next_follow_up) : "-"}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(lead.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <QuickActions
                          phone={lead.phone}
                          onCall={() => handleCall(lead, true)}
                          onViewHistory={() => handleViewHistory(lead)}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Call Log Modal */}
      <CallLogModal
        open={!!callLogContact}
        onClose={() => setCallLogContact(null)}
        contact={callLogContact}
        onSuccess={fetchLeads}
      />

      {/* Call History Modal */}
      <CallHistoryModal
        open={!!historyContact}
        onClose={() => setHistoryContact(null)}
        contactId={historyContact?.id}
        contactType={historyContact?.type}
        contactName={historyContact?.name}
      />
    </div>
  );
};

export default LeadsPage;
